import { useMemo } from "react"

import { useApolloClient } from "@apollo/client"

import { times } from "shared/lib/times"
import { ProjectChild } from "shared/types/Project"
import { SelectOption } from "shared/types/Select"

import { readProjectParentFragment } from "entities/project-children/lib/readProjectParentFragment"
import { useProjectData } from "entities/projects/api/useProjectData"

interface UseProjectPositionSortingArgs {
  projectID: string | null
  parentID?: string | null
  childID?: string | null
  childType: Exclude<ProjectChild["__typename"], undefined>
}

export function useProjectPositionOptions({
  projectID,
  parentID,
  childID,
  childType
}: UseProjectPositionSortingArgs) {
  const { cache } = useApolloClient()

  const { project, isLoading: isLoadingChildren } = useProjectData(
    projectID,
    true
  )

  const children = useMemo(() => {
    if (!projectID) return []

    const parent = readProjectParentFragment(parentID || projectID, cache)
    return (
      parent?.children?.filter(child => child.__typename === childType) || []
    )
  }, [project?.children, projectID, parentID])

  const currentPosition = useMemo(() => {
    return children.findIndex(child => child.id === childID)
  }, [children, childID])

  const positionOptions: SelectOption<number>[] = useMemo(() => {
    return times(
      currentPosition === -1 ? children.length + 1 : children.length,
      index => ({
        key: index.toString(),
        value: index,
        label: (index + 1).toString()
      })
    )
  }, [childID, children])

  return {
    isLoading: isLoadingChildren,
    currentPosition,
    positionOptions
  }
}
